import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppSettings } from '../context/AppSettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

export const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { app_name, logo_base64 } = useAppSettings();
    const displayName = app_name || 'Plaster Survey';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Please fill in all fields');
            return;
        }

        setLoading(true);
        try {
            await login(email, password);
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            {/* Hero Section */}
            <div 
                className="relative h-48 bg-cover bg-center"
                style={{ 
                    backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.9)), url('/hero-bg.jpg')` 
                }}
            >
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                    <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center mb-4 shadow-lg overflow-hidden">
                        {logo_base64
                            ? <img src={`data:image/png;base64,${logo_base64}`} alt="Logo" className="w-full h-full object-contain" />
                            : <span className="text-2xl font-bold">PS</span>
                        }
                    </div>
                    <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
                        {displayName}
                    </h1>
                    <p className="text-slate-300 text-sm mt-1">Professional Quoting Tool</p>
                </div>
            </div>

            {/* Login Form */}
            <div className="flex-1 px-4 -mt-8 pb-8">
                <Card className="max-w-md mx-auto shadow-xl border-0">
                    <CardHeader className="text-center pb-4">
                        <CardTitle className="font-heading text-2xl uppercase tracking-tight">
                            Sign In
                        </CardTitle>
                        <CardDescription>
                            Enter your credentials to access the app
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Email Address
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="h-12 border-slate-300 focus:border-red-600 focus:ring-red-600"
                                    data-testid="login-email"
                                    autoComplete="email"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Password
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="h-12 border-slate-300 focus:border-red-600 focus:ring-red-600 pr-12"
                                        data-testid="login-password"
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide shadow-sm"
                                data-testid="login-submit"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        Signing In...
                                    </>
                                ) : (
                                    'Sign In'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <p className="text-slate-600">
                                Don't have an account?{' '}
                                <Link 
                                    to="/register" 
                                    className="text-red-600 font-semibold hover:underline"
                                    data-testid="register-link"
                                >
                                    Register
                                </Link>
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Footer */}
            <div className="text-center py-4 text-sm text-slate-500">
                <p>Plaster Survey & Quote v1.0</p>
            </div>
        </div>
    );
};
